import type { Workbook } from "exceljs"
import type { FetchResults, FeedingSummaryRow, VerbrauchStats } from "./types"

// ── Config ─────────────────────────────────────────────────────────────

const WEIGHT_FMT = '#,##0.0 "kg"'
// const WEIGHT_FMT = '0 "kg"';
const MINUTES_FMT = '0 "Min."'
const PERCENTAGE_FMT = "0.0%"
const NUM_FMT = "#,##0.00"
/** @todo Get currency symbol from org settings */
const CURRENCY_FMT = "#,##0 €"
const COUNT_FMT = "#,##0"
const DETAILS_SHEET_NAME = "Details"

// ── Style palette ──────────────────────────────────────────────────────────────

const C = {
  headerFill: "FF498E39",
  headerFont: "FFFFFFFF",
  failFill: "FFFFC7CE",
  failFont: "FF9C0006",
  altFill: "FFEAF4E7",
  border: "FFD0D7E0",
  linkFont: "FF1F3864",
}

// ── Excel utils ──────────────────────────────────────────────────────────────

function applyHeaderStyle(row: any, colCount: number) {
  row.height = 20
  row.eachCell({ includeEmpty: true }, (cell: any, col: number) => {
    if (col > colCount) return
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerFill },
    }
    cell.font = { bold: true, color: { argb: C.headerFont }, size: 11 }
    cell.alignment = { vertical: "middle", horizontal: "center" }
    cell.border = { bottom: { style: "medium", color: { argb: C.border } } }
  })
}

function applyDataRow(
  row: any,
  colCount: number,
  failed: boolean,
  alt: boolean
) {
  row.eachCell({ includeEmpty: true }, (cell: any, col: number) => {
    if (col > colCount) {
      return
    }
    if (failed) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.failFill },
      }
      cell.font = { color: { argb: C.failFont } }
    } else if (alt) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.altFill },
      }
    }
    cell.border = { bottom: { style: "thin", color: { argb: C.border } } }
  })
}

function autoWidth(ws: any, minW = 8, maxW = 40) {
  ws.columns.forEach((col: any) => {
    let max = minW
    col.eachCell({ includeEmpty: false }, (cell: any) => {
      const v = cell.value
      const text = v && typeof v === "object" && "text" in v ? v.text : v
      const len = text ? String(text).length : 0
      if (len > max) {
        max = len
      }
    })
    col.width = Math.min(max + 2, maxW)
  })
}

// ── Feedings summary sheet ─────────────────────────────────────────────────────────────

export function buildFeedingsSummarySheet(
  wb: Workbook,
  rows: FeedingSummaryRow[],
  results: FetchResults
) {
  const ws = wb.addWorksheet("Summary")

  ws.columns = [
    { header: "Rezeptname", key: "recipename" },
    { header: "Timestamp", key: "timestamp" },
    { header: "Arb. Zeit", key: "duration", numFmt: WEIGHT_FMT },
    { header: "Fahrer", key: "driver" },
    { header: "Geplant", key: "planned", numFmt: WEIGHT_FMT },
    { header: "Ladung", key: "actual_lad", numFmt: WEIGHT_FMT },
    { header: "Fehler", key: "error_lad", numFmt: PERCENTAGE_FMT },
    { header: "Abladung", key: "actual_abl", numFmt: WEIGHT_FMT },
    { header: "Fehler", key: "error_abl", numFmt: PERCENTAGE_FMT },
    { header: "Export Status", key: "status" },
  ]

  applyHeaderStyle(ws.getRow(1), ws.columns.length)
  ws.views = [{ state: "frozen", ySplit: 1 }]

  rows
    .sort((a, b) =>
      a.Start.localeCompare(b.Start, undefined, {
        sensitivity: "base",
        numeric: true,
      })
    )
    .forEach((row, i) => {
      const res = results.get(row.FeedingReportId)
      const failed = !res || res.status === "failed"
      const errorMsg = failed ? ((res as any)?.error ?? "Not fetched") : ""

      /** @todo Use userPrefLang */
      const timestamp =
        new Date(row.Start).toLocaleDateString("de-de", {
          dateStyle: "short",
        }) +
        " " +
        new Date(row.Start).toLocaleTimeString("de-de", {
          timeStyle: "short",
        }) +
        " - " +
        new Date(row.End).toLocaleTimeString("de-de", {
          timeStyle: "short",
        })

      const wsRow = ws.addRow({
        recipename: row.Recipename,
        timestamp: timestamp,
        driver: row.Driver,
        planned: row.LoadingTarget,
        actual_lad: row.Loaded,
        error_lad: row.LoadingRatio / 100,
        actual_abl: row.Unloaded,
        error_abl: row.UnloadingRatio / 100,
        duration: row.Duration,
        status: failed ? `⚠ ${errorMsg}` : "✓ OK",
      })
      wsRow.getCell("planned").numFmt = WEIGHT_FMT
      wsRow.getCell("actual_lad").numFmt = WEIGHT_FMT
      wsRow.getCell("error_lad").numFmt = PERCENTAGE_FMT
      wsRow.getCell("actual_abl").numFmt = WEIGHT_FMT
      wsRow.getCell("error_abl").numFmt = PERCENTAGE_FMT
      wsRow.getCell("duration").numFmt = MINUTES_FMT
      applyDataRow(wsRow, ws.columns.length, failed, i % 2 === 1)
    })

  autoWidth(ws)
}

// ── Feedings details sheet (long format: one row per Loading, one row per Unloading) ───────────

export function buildFeedingsDetailsSheet(
  wb: Workbook,
  rows: FeedingSummaryRow[],
  results: FetchResults
) {
  const ws = wb.addWorksheet(DETAILS_SHEET_NAME)

  ws.columns = [
    { header: "Rezeptname", key: "recipename" },
    { header: "Start, Ende", key: "timestamp" },
    { header: "Arb. Zeit", key: "duration", numFmt: MINUTES_FMT },
    { header: "Fahrer", key: "driver" },
    { header: "Futtermittel", key: "feed_component" },
    { header: "Ausladegruppe", key: "animal_group" },
    { header: "Geplant", key: "planned", numFmt: WEIGHT_FMT },
    { header: "Ladung", key: "actual", numFmt: NUM_FMT },
    { header: "Abweichung", key: "error", numFmt: NUM_FMT },
    { header: "Kosten", key: "cost", numFmt: CURRENCY_FMT },
    { header: "Tieranzahl", key: "count", numFmt: COUNT_FMT },
  ]

  applyHeaderStyle(ws.getRow(1), ws.columns.length)
  ws.views = [{ state: "frozen", ySplit: 1 }]

  // Sort rows by timestamp ascending; failed rows are omitted entirely from
  // this sheet (they're already flagged on the Summary sheet).
  const sortedRows = [...rows]
    .filter((r) => {
      const res = results.get(r.FeedingReportId)
      return res?.status === "ok"
    })
    .sort((a, b) =>
      a.Start.localeCompare(b.Start, undefined, {
        sensitivity: "base",
        numeric: true,
      })
    )

  let altToggle = false

  sortedRows.forEach((row) => {
    const res = results.get(row.FeedingReportId)
    if (!res || res.status !== "ok") {
      // type guard, already filtered above
      return
    }
    const feeding = res.response.data

    /** @todo Use userPrefLang */
    const timestamp =
      new Date(feeding.Startzeit).toLocaleDateString("de-de", {
        dateStyle: "short",
      }) +
      " " +
      new Date(feeding.Startzeit).toLocaleTimeString("de-de", {
        timeStyle: "short",
      }) +
      " - " +
      new Date(feeding.Endzeit).toLocaleTimeString("de-de", {
        timeStyle: "short",
      })

    const totalNumberOfAnimals = feeding.Ausladegruppen.reduce((prev, curr) => {
      return prev + curr.Tieranzahl
    }, 0)
    const totalMass = feeding.Komponenten.reduce((prev, curr) => {
      return prev + curr.Menge
    }, 0)
    const massPerAnimal = totalMass / totalNumberOfAnimals

    feeding.AufgenMeng.forEach((ld) => {
      const fc = feeding.Komponenten.find((fc) => fc.id === ld.id)!
      const error = (ld.Menge / fc.Menge - 1) * 100
      const costPerKg = fc.price_per_kg ?? 0
      const cost = costPerKg * ld.Menge
      const wsRow = ws.addRow({
        recipename: row.Recipename,
        timestamp: timestamp,
        duration: row.Duration,
        driver: row.Driver,
        feed_component: fc.Bezeichnung,
        animal_group: "",
        planned: fc.Menge,
        actual: ld.Menge,
        error: error / 100,
        cost: cost,
        count: "",
      })
      wsRow.getCell("duration").numFmt = MINUTES_FMT
      wsRow.getCell("planned").numFmt = WEIGHT_FMT
      wsRow.getCell("actual").numFmt = WEIGHT_FMT
      wsRow.getCell("error").numFmt = PERCENTAGE_FMT
      wsRow.getCell("cost").numFmt = CURRENCY_FMT
      // wsRow.getCell("count").numFmt = COUNT_FMT
      applyDataRow(wsRow, ws.columns.length, false, altToggle)
    })

    feeding.AusgebrMeng.forEach((unl) => {
      const ag = feeding.Ausladegruppen.find((ag) => ag.id === unl.id)!
      const unloadingTarget = massPerAnimal * ag.Tieranzahl
      const error = (unl.Menge / unloadingTarget - 1) * 100
      const wsRow = ws.addRow({
        recipename: row.Recipename,
        timestamp: timestamp,
        duration: row.Duration,
        driver: row.Driver,
        feed_component: "",
        animal_group: ag.Bezeichnung,
        planned: unloadingTarget,
        actual: unl.Menge,
        error: error / 100,
        cost: "",
        count: ag.Tieranzahl,
      })
      wsRow.getCell("duration").numFmt = MINUTES_FMT
      wsRow.getCell("planned").numFmt = WEIGHT_FMT
      wsRow.getCell("actual").numFmt = WEIGHT_FMT
      wsRow.getCell("error").numFmt = PERCENTAGE_FMT
      // wsRow.getCell("cost").numFmt = CURRENCY_FMT
      wsRow.getCell("count").numFmt = COUNT_FMT
      applyDataRow(wsRow, ws.columns.length, false, altToggle)
    })

    // Alternate shading per-run (not per-row) so each run's block is visually grouped
    altToggle = !altToggle
  })

  autoWidth(ws)
}

// ── Verbrauch summary sheet ───────────────────────────────────────────────────

export function buildVerbrauchSummarySheet(
  wb: Workbook,
  stats: VerbrauchStats
) {
  const ws = wb.addWorksheet("Summary")

  ws.columns = [
    { header: "Futtermittel", key: "name" },
    { header: "Gewicht", key: "weight" },
  ]

  applyHeaderStyle(ws.getRow(1), ws.columns.length)
  ws.views = [{ state: "frozen", ySplit: 1 }]

  stats.total_cons.forEach((e, i) => {
    const wsRow = ws.addRow({ name: e.Komponentenname, weight: e.Abladung })
    wsRow.getCell("weight").numFmt = WEIGHT_FMT
    applyDataRow(wsRow, ws.columns.length, false, i % 2 === 1)
  })

  autoWidth(ws)
}
